/**
 * EventOfflineControls — Controles de caché offline POR EVENTO, reutilizable.
 * Disponible para cualquier DJ (no solo admin). Se usa en el detalle del evento
 * (calendario) y en el "Modo en Vivo".
 *
 * - "Preparar offline": descarga TODOS los MP3 del evento a ESTE dispositivo
 *   (con progreso) y protege la caché (navigator.storage.persist).
 * - "Borrar de mi dispositivo": SOLO se muestra para eventos YA PASADOS
 *   (nunca antes/durante el evento) y NUNCA toca el servidor (solo IndexedDB local).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { DownloadCloud, Trash2, Loader2, ShieldCheck } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { BACKEND_URL } from '../config';
import { getEventAudios, downloadEventAudios, deleteEventAudios } from '../utils/audioCache';
import { BulkYouTubePrepareButton } from './BulkYouTubePrepareButton'; // eslint-disable-line no-unused-vars

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// Pasado = el día del evento es anterior a HOY (nunca el mismo día ni antes del evento).
const isEventPast = (eventDate) => {
  if (!eventDate) return false;
  const d = new Date(eventDate);
  if (isNaN(d.getTime())) return false;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const ev = new Date(d); ev.setHours(0, 0, 0, 0);
  return ev < today;
};

export const EventOfflineControls = ({ playlistId, eventDate, peticiones }) => {
  const [cached, setCached] = useState({ count: 0, mb: '0' });
  const [prep, setPrep] = useState(null); // { completed, total }
  const [deleting, setDeleting] = useState(false);

  const refresh = useCallback(async () => {
    if (!playlistId) return;
    const list = await getEventAudios(playlistId);
    const bytes = list.reduce((s, a) => s + (a.size || 0), 0);
    setCached({ count: list.length, mb: (bytes / 1024 / 1024).toFixed(1) });
  }, [playlistId]);

  useEffect(() => {
    let on = true;
    Promise.resolve().then(() => { if (on) refresh(); });
    return () => { on = false; };
  }, [refresh]);

  const downloadableCount = (peticiones || []).filter((p) => p.audio_file_id).length;
  const past = isEventPast(eventDate);

  const prepareOffline = async () => {
    setPrep({ completed: 0, total: downloadableCount });
    let lastErr = null;
    try {
      let pets = peticiones;
      if (!pets || pets.length === 0) {
        const r = await axios.get(`${BACKEND_URL}/api/playlists/${playlistId}`, { headers: authHeaders() });
        pets = r.data.peticiones || [];
      }
      const withAudio = pets.filter((p) => p.audio_file_id);
      if (withAudio.length === 0) {
        toast.message('Este evento no tiene MP3 descargables');
        return;
      }
      const token = localStorage.getItem('token');
      const result = await downloadEventAudios(
        playlistId, pets, `${BACKEND_URL}/api`, token,
        (p) => {
          setPrep({ completed: p.completed, total: p.total });
          if (p.lastError) lastErr = p.lastError;
        },
      );
      if (navigator.storage && navigator.storage.persist) {
        await navigator.storage.persist();
      }
      if (result.errors > 0) {
        // Feedback específico si sabemos la causa (403/401/404) — mucho más
        // útil que el genérico "N con error" que ocultaba el problema real.
        const reasonHint = lastErr ? ` — Motivo: ${lastErr}` : '';
        toast.error(
          `⚠️ ${result.completed}/${result.total} MP3 guardados (${result.errors} con error)${reasonHint}`,
          { duration: 8000 }
        );
      } else {
        toast.success(`✅ ${result.completed}/${result.total} MP3 guardados en este dispositivo. Caché protegida.`);
      }
      await refresh();
    } catch (e) {
      toast.error('Error preparando el evento para offline');
    } finally {
      setPrep(null);
    }
  };

  const deleteFromDevice = async () => {
    if (!window.confirm('¿Borrar de ESTE dispositivo los MP3 descargados de este evento?\n\nNo afecta al servidor ni a otros dispositivos.')) return;
    setDeleting(true);
    try {
      await deleteEventAudios(playlistId);
      toast.success('📱 Descargas de este evento borradas de este dispositivo');
      await refresh();
    } catch (e) {
      toast.error('Error al borrar del dispositivo');
    } finally {
      setDeleting(false);
    }
  };

  // Nada que mostrar si no hay MP3 descargables ni nada en caché
  // (el botón "Preparar YT (N)" externo cubre las peticiones sin MP3)
  if (downloadableCount === 0 && cached.count === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="event-offline-controls">
      {cached.count > 0 && (
        <span
          className="text-xs bg-green-900/30 text-green-400 px-2 py-1 rounded whitespace-nowrap flex items-center gap-1"
          data-testid="event-offline-cached-badge"
        >
          <ShieldCheck className="w-3 h-3" /> {cached.count} en este dispositivo ({cached.mb} MB)
        </span>
      )}
      {downloadableCount > 0 && (
        <Button
          onClick={prepareOffline}
          disabled={!!prep}
          size="sm"
          className="bg-cyan-600 hover:bg-cyan-700 text-white text-xs"
          title="Descargar todos los MP3 del evento a este dispositivo y proteger la caché (úsalo el día antes)"
          data-testid="event-offline-prepare-btn"
        >
          {prep ? (
            <><Loader2 className="w-4 h-4 mr-1 animate-spin" />{prep.completed}/{prep.total}</>
          ) : (
            <><DownloadCloud className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Preparar offline</span></>
          )}
        </Button>
      )}
      {past && cached.count > 0 && (
        <Button
          onClick={deleteFromDevice}
          disabled={deleting}
          size="sm"
          variant="outline"
          className="border-slate-600 text-slate-400 hover:bg-slate-700 text-xs"
          title="Borrar de este dispositivo las descargas de este evento (evento ya celebrado)"
          data-testid="event-offline-delete-btn"
        >
          {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
        </Button>
      )}
    </div>
  );
};

export default EventOfflineControls;
